import React from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import Calendar from '../Calendar'
import t from 'lib/translations'
import { bookingHasPremiumService, getBookingState } from 'lib/bookingStateHelper'

import './index.css'

const DATE_FORMAT = 'YYYY-MM-DD'

// All dates coming in via props are considered UTC, as they are
// fetched from a server. All UTC dates must be converted to a local date
// object before fed into the calendar. It will hopefully not be a performance
// issue as all events/availabilities are lazy loaded based on the current
// calendar view.
const BookingsCalendar = ({
  isFetchingData,
  bookings,
  availability,
  onSelectEvent,
  ...rest
}) => {

  const events = bookings.map(({node: booking}) => {
    const startsAt = moment(booking.startsAt)
    const endsAt = moment(booking.endsAt)

    return {
      booking,
      start: startsAt.toDate(),
      end: endsAt.toDate(),
    }
  })

  const availabilityDates = availability.map(({node}) => ({
    // It's not possible to set an availability across multiple days
    // in iweb, so I will base availability date on startsAt only.
    // The calendar will find availabilities for a given day based on this field.
    date: moment(node.startsAt).format(DATE_FORMAT),
    startsAt: moment(node.startsAt),
    endsAt: moment(node.endsAt),
    availability: node,
  }))

  const DateCellWithAvailability = (props) => {
    if (isFetchingData) {
      return props.children
    }

    const cellDateString = moment(props.value).format(DATE_FORMAT)
    const availability = availabilityDates.find(({date}) =>
      date === cellDateString
    )

    return (
      <div className={[
        props.children.props.className,
        availability ? 'available' : 'unavailable',
      ].join(' ')} />
    )
  }

  const TimeSlotWithAvailability = ({children, value}) => {
    // If there are children within children.props, this will render
    // the left margin with times. Availability should only be rendered
    // if the column represents a day.
    if (isFetchingData || children.props.children) {
      return children
    }

    const timeSlotTime = moment(value)

    // Find availability matching time slot. "[)" means left inclusive,
    // which means that startsAt can be the same, while endsAt must be less.
    const availability = availabilityDates.find(({startsAt, endsAt}) =>
      timeSlotTime.isBetween(startsAt, endsAt, 'minutes', '[)')
    )

    const className = availability ? 'available' : 'unavailable'

    return <div className={`rbc-time-slot ${className}`} />
  }

  const EventWrapper = ({ children, event }) => {
    const isPremium = bookingHasPremiumService(event.booking)
    return (
      <div className={isPremium ? 'event premium-service' : ''}>
        {children}
      </div>
    )
  }

  const BasicEvent = ({ event }) => {
    const { booking } = event
    const formattedStartsAt = moment(booking.startsAt).format('HH:mm')
    const formattedEndsAt = moment(booking.endsAt).format('HH:mm')
    return `${formattedStartsAt} - ${formattedEndsAt}`
  }

  const DetailedEvent = ({ event }) => {
    const { booking } = event;
    const formattedStartsAt = moment(booking.startsAt).format('HH:mm')
    const formattedEndsAt = moment(booking.endsAt).format('HH:mm')
    return (
      <div className="detailed-booking">
        <h4>{booking.estate.streetAddress}, {booking.estate.postalCode} {booking.estate.postalArea}</h4>
        <p>{booking.products.edges.map(product => `${(product.node.quantity ? product.node.quantity : '')} ${product.node.name}`).join(', ')}</p>
        <p>Status: <span className={`state ${getBookingState(booking)}`}>{t(`bookingState.${getBookingState(booking)}`)}</span></p>
        <p>{formattedStartsAt} - {formattedEndsAt}</p>
      </div>
    )
  }

  return (
    <Calendar
      {...rest}
      step={15}
      events={events}
      views={['month', 'week', 'day']}
      onSelectEvent={onSelectEvent}
      components={{
        event: DetailedEvent,
        eventWrapper: EventWrapper,
        dateCellWrapper: DateCellWithAvailability,
        timeSlotWrapper: TimeSlotWithAvailability,
        month: {
          event: BasicEvent
        },
      }}
    />
  )
}

BookingsCalendar.localizer = Calendar.localizer

BookingsCalendar.propTypes = {
  bookings: PropTypes.array.isRequired,
  onSelectEvent: PropTypes.func,
  availability: PropTypes.array,
  isFetchingData: PropTypes.bool,
}

export default BookingsCalendar
